/**
 * Tipo para objetos que podem ser mesclados profundamente
 */
type DeepMergeable = Record<string | number | symbol, any>;

/**
 * Tipo recursivo para merge profundo
 */
type DeepMerge<T, U> = {
	[K in keyof (T & U)]: K extends keyof U
		? K extends keyof T
			? T[K] extends DeepMergeable
				? U[K] extends DeepMergeable
					? DeepMerge<T[K], U[K]>
					: U[K]
				: U[K]
			: U[K]
		: K extends keyof T
			? T[K]
			: never;
};

/**
 * Verifica se um valor é um objeto plano (não array, não null, não Date, etc.)
 */
function isPlainObject(value: unknown): value is DeepMergeable {
	return (
		value !== null &&
		typeof value === 'object' &&
		!Array.isArray(value) &&
		!(value instanceof Date) &&
		!(value instanceof RegExp) &&
		value.constructor === Object
	);
}

/**
 * Faz merge profundo de dois objetos, preservando estruturas aninhadas
 * @param target Objeto de destino
 * @param source Objeto de origem (tem prioridade em conflitos)
 * @returns Novo objeto com merge profundo aplicado
 */
export function deepMerge<T extends DeepMergeable, U extends DeepMergeable>(
	target: T,
	source: U
): DeepMerge<T, U> {
	const result = { ...target } as any;

	for (const key in source) {
		const sourceValue = source[key];
		const targetValue = result[key];

		if (isPlainObject(sourceValue) && isPlainObject(targetValue)) {
			result[key] = deepMerge(targetValue, sourceValue);
		} else {
			result[key] = sourceValue;
		}
	}

	return result;
}

/**
 * Faz merge profundo de múltiplos objetos
 * @param objects Lista de objetos para fazer merge (ordem importa - últimos têm prioridade)
 * @returns Novo objeto com merge profundo de todos os objetos
 */
export function deepMergeMultiple<T extends DeepMergeable[]>(
	...objects: T
): T extends [infer First, ...infer Rest]
	? Rest extends DeepMergeable[]
		? Rest['length'] extends 0
			? First
			: DeepMerge<First, ReturnType<typeof deepMergeMultiple<Rest>>>
		: First
	: {} {
	return objects.reduce((acc, obj) => deepMerge(acc, obj), {} as any);
}

/**
 * Tipo específico para objetos de locale/i18n
 */
interface LocaleObject {
	[locale: string]: {
		[key: string]: any;
	};
}

/**
 * Faz merge de objetos de locale/tradução com tipagem específica para i18n
 * @param target Objeto de locale de destino
 * @param source Objeto de locale de origem (tem prioridade)
 * @returns Novo objeto com merge profundo aplicado
 */
export function deepMergeLocales<
	T extends LocaleObject,
	U extends LocaleObject
>(target: T, source: U): DeepMerge<T, U> {
	return deepMerge(target, source);
}
